import { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, FolderOpen, PlusSquare, Save, Command, Terminal, Sidebar, PanelRight, LogOut } from 'lucide-react'

const ALL_COMMANDS = [
  { id: 'save',           label: 'Save File',              shortcut: 'Ctrl+S',       icon: Save,       action: 'save' },
  { id: 'open-project',   label: 'Open Project',           shortcut: '',              icon: FolderOpen, action: 'openProject' },
  { id: 'new-project',    label: 'New Project',            shortcut: '',              icon: PlusSquare, action: 'createProject' },
  { id: 'toggle-sidebar', label: 'Toggle Sidebar',         shortcut: 'Ctrl+B',       icon: Sidebar,    action: 'toggleSidebar' },
  { id: 'toggle-ai',      label: 'Toggle AI Panel',        shortcut: '',              icon: PanelRight, action: 'toggleAi' },
  { id: 'toggle-terminal',label: 'Toggle Terminal',         shortcut: 'Ctrl+`',       icon: Terminal,   action: 'toggleTerminal' },
  { id: 'reset-setup',    label: 'Reset Setup Wizard',     shortcut: '',              icon: Command,    action: 'resetSetup' },
  { id: 'logout',         label: 'Sign Out',               shortcut: '',              icon: LogOut,     action: 'logout' },
]

export function CommandPalette({ open, onClose, onAction }) {
  const [query, setQuery] = useState('')
  const [focusIndex, setFocusIndex] = useState(0)
  const inputRef = useRef(null)

  const filtered = query.trim()
    ? ALL_COMMANDS.filter(cmd => cmd.label.toLowerCase().includes(query.toLowerCase()))
    : ALL_COMMANDS

  useEffect(() => {
    if (open) {
      setQuery('')
      setFocusIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  const handleSelect = useCallback((cmd) => {
    onAction(cmd.action)
    onClose()
  }, [onAction, onClose])

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusIndex(i => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered[focusIndex]) {
        handleSelect(filtered[focusIndex])
      }
    }
  }

  useEffect(() => {
    setFocusIndex(0)
  }, [query])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="command-palette-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
        >
          <motion.div
            className="command-palette"
            initial={{ opacity: 0, y: -20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="command-palette-input">
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a command…"
                autoFocus
              />
            </div>
            <div className="command-palette-list">
              {filtered.length === 0 && (
                <div className="command-palette-empty">No commands match "{query}"</div>
              )}
              {filtered.map((cmd, i) => {
                const Icon = cmd.icon
                return (
                  <div
                    key={cmd.id}
                    className={`command-palette-item ${i === focusIndex ? 'focused' : ''}`}
                    onClick={() => handleSelect(cmd)}
                    onMouseEnter={() => setFocusIndex(i)}
                  >
                    <span className="command-palette-item-icon">
                      <Icon size={15} strokeWidth={1.5} />
                    </span>
                    <span className="command-palette-item-label">{cmd.label}</span>
                    {cmd.shortcut && (
                      <span className="command-palette-item-shortcut">{cmd.shortcut}</span>
                    )}
                  </div>
                )
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
